import { useCallback, useRef, useState } from 'react';
import type { AiTextDetectionResult } from '../../../../domain/aiTextDetection';
import { detectAiTextForAdmin } from '../../../../services/aiDetection';
import { logGlobalError } from '../../../../utils/globalErrorLogger';

// Mirrors MAX_TEXT_CHARS in functions/detectAiTextForAdmin.js.
export const MAX_TEXT_CHARS = 100_000;

// Callable codes that describe the input or a vendor limit, not a bug.
const EXPECTED_ERROR_CODES = new Set([
  'functions/invalid-argument',
  'functions/resource-exhausted',
]);

export interface DetectionToolViewModel {
  busy: boolean;
  analyzing: boolean;
  error: string | null;
  text: string;
  result: AiTextDetectionResult | null;
  setText: (text: string) => void;
  initialize: () => Promise<void>;
  analyze: () => Promise<void>;
  clear: () => void;
}

export function useDetectionToolViewModel(): DetectionToolViewModel {
  const [busy, setBusy] = useState<boolean>(true);
  const [analyzing, setAnalyzing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [text, setText] = useState<string>('');
  const [result, setResult] = useState<AiTextDetectionResult | null>(null);
  const runSeqRef = useRef<number>(0);

  // Nothing to load: the tool starts empty and only calls Pangram on demand.
  const initialize = useCallback(async () => {
    setBusy(true);
    setBusy(false);
  }, []);

  const analyze = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed || trimmed.length > MAX_TEXT_CHARS) return;
    const seq = ++runSeqRef.current;
    setAnalyzing(true);
    setError(null);
    try {
      const detection = await detectAiTextForAdmin(trimmed);
      if (seq === runSeqRef.current) setResult(detection);
    } catch (e: unknown) {
      if (seq !== runSeqRef.current) return;
      setError(e instanceof Error ? e.message : 'Pangram detection failed.');
      const code = (e as { code?: unknown })?.code;
      if (typeof code !== 'string' || !EXPECTED_ERROR_CODES.has(code)) {
        logGlobalError(e, { context: 'AiDetectionTool.analyze', metadata: { chars: trimmed.length } });
      }
    } finally {
      if (seq === runSeqRef.current) setAnalyzing(false);
    }
  }, [text]);

  const clear = useCallback(() => {
    runSeqRef.current += 1;
    setText('');
    setResult(null);
    setError(null);
    setAnalyzing(false);
  }, []);

  return { busy, analyzing, error, text, result, setText, initialize, analyze, clear };
}
